import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  Dimensions,
  Alert,
  Platform,
  TextStyle,
  Modal,
  ActivityIndicator,
  Animated,
  LayoutAnimation,
  UIManager,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import * as ImagePicker from 'expo-image-picker';
import * as MediaLibrary from 'expo-media-library';
import * as Sharing from 'expo-sharing';
import ViewShot from 'react-native-view-shot';
import Button from '../../components/common/Button';
import { RootStackParamList } from '../../types/navigation';
import { useSubscription } from '../../hooks/useSubscription';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { CameraComponent } from '../../components/shared/CameraComponent';
import { useSkinUndertone } from '../../hooks/useSkinUndertone';
import DynamicColorWheel from '../../components/shared/DynamicColorWheel';
import JewelryMetalSwatch, {
  getJewelryMetalHex,
} from '../../components/shared/JewelryMetalSwatch';
import { LinearGradient } from 'expo-linear-gradient';
import palettes from '../../assets/color-palettes.json';
import { palette } from '../../utils/palette';
import { resolveFaceAnalysisForUndertone } from '../../utils/faceRoi';
import {
  legacyUndertoneForApi,
  parseUndertoneFromStorage,
} from '../../utils/seasonUndertone';
import {
  skinUndertoneService,
  ColorAnalysisResult,
} from '../../services/SkinUndertoneService';
import {
  faceDetectionService,
  FaceDetectionResult,
} from '../../services/FaceDetectionService';
import {
  getRecentAnalysis,
  getAIColorRecommendations,
} from '../../api/colours';
import { usersAPI } from '../../api/users';
import { trackButtonClick } from '../../utils/analytics';
import { useQueryClient } from '@tanstack/react-query';
import { authKeys } from '../../hooks/useAuth';
import BlurredLockOverlay from '../../components/shared/BlurredLockOverlay';
import FloatingActionButton, {
  FloatingActionButtonOverlay,
} from '../../components/shared/FloatingActionButton';
import PremiumAIColorChatModal from '../../components/shared/PremiumAIColorChatModal';
import { PageHero } from '../../components';

const ENABLE_AI_FEATURES = false;

// Enable LayoutAnimation for smooth transitions
if (
  Platform.OS === 'android' &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

// Define a flexible type for our palette objects.
interface PaletteType {
  id: string;
  undertone: string;
  name: string;
  description: string;
  colors: string[];
  avoidColors?: string[];
  makeupRecommendations?: Record<string, unknown>;
  /** Accent swatches from color-palettes.json (e.g. accent1, accent2, crystal). */
  jewelry?: Record<string, string>;
}

type SeasonalPaletteKey = 'spring' | 'summer' | 'autumn' | 'winter';

// Combine all season palettes into a single undertone-normalized list.
const allPalettes: PaletteType[] = (
  ['spring', 'summer', 'autumn', 'winter'] as SeasonalPaletteKey[]
).flatMap((season) => {
  const seasonPalettes = palettes[season] ?? [];
  return seasonPalettes.map((p, index) => ({
    ...p,
    undertone: season,
    id: `${season}-${index}-${p.name.toLowerCase().replace(/\s+/g, '-')}`,
  })) as PaletteType[];
});

const JEWELRY_LABELS: Record<string, string> = {
  accent1: 'Accent 1',
  accent2: 'Accent 2',
  crystal: 'Crystal',
};

function jewelryLabelForKey(key: string): string {
  if (JEWELRY_LABELS[key]) return JEWELRY_LABELS[key];
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Get screen dimensions for responsive layout

type ImageSourceType = 'camera' | 'upload' | 'drag' | 'analysis';

interface VirtualDrapeTestScreenProps {
  initialView?: 'upload' | 'results';
  initialViewRequestId?: number;
  /** When true, shows full feature with results blurred; used for public preview without sign-in */
  isPublicPreview?: boolean;
}

const VirtualDrapeTestScreen = ({
  initialView,
  initialViewRequestId,
  isPublicPreview = false,
}: VirtualDrapeTestScreenProps) => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute();
  const { isPremium } = useSubscription();
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { isMobile, containerPadding } = useResponsiveLayout();
  const queryClient = useQueryClient();

  // Calculate available height excluding bottom navigation
  const screenHeight = Dimensions.get('window').height;
  const bottomNavHeight = isMobile ? 80 + insets.bottom + 10 : 0;
  const availableHeight = isMobile
    ? screenHeight - bottomNavHeight
    : screenHeight;

  // Dynamic wheel size based on device type (larger wheel)
  const WHEEL_SIZE = isMobile ? 300 : 450;
  const IMAGE_SIZE = WHEEL_SIZE * 0.5;

  const [faceImage, setFaceImage] = useState<string | null>(null);
  const [selectedPalette, setSelectedPalette] = useState<PaletteType>(
    allPalettes[0]
  );
  const [wheelColors, setWheelColors] = useState<string[]>(
    allPalettes[0].colors
  );
  const [selectedColor, setSelectedColor] = useState<string | null>(
    allPalettes[0].colors[0]
  );
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [isShowingRecommendedColors, setIsShowingRecommendedColors] =
    useState(false);
  const [isAIPaletteLoading, setIsAIPaletteLoading] = useState(false);
  const aiPaletteImageRef = useRef<string | null>(null);
  const [isCreatingCard, setIsCreatingCard] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [isCameraMode, setIsCameraMode] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future use
  const [skinTone, setSkinTone] = useState<{
    category: string;
    confidence: number;
    color: string;
  } | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for feedback feature
  const [userFeedback, setUserFeedback] = useState<string>('');
  const [_showFeedbackModal, _setShowFeedbackModal] = useState(false);
  const [_selectedFeedback, _setSelectedFeedback] = useState<
    'love' | 'like' | 'dislike' | null
  >(null);
  const [croppedFaceImage, setCroppedFaceImage] = useState<string | null>(null);
  const [faceDetectionResult, setFaceDetectionResult] =
    useState<FaceDetectionResult | null>(null);
  const [isDetectingFace, setIsDetectingFace] = useState(false);
  const [faceDetectionError, setFaceDetectionError] = useState<string | null>(
    null
  );
  const [imageSourceType, setImageSourceType] =
    useState<ImageSourceType | null>(null);

  // Simple cache for face detection results
  const faceDetectionCache = useRef<
    Map<string, { result: FaceDetectionResult; croppedImage: string }>
  >(new Map());
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));
  const [scaleAnim] = useState(new Animated.Value(0.95));
  const [colorAnalysis, setColorAnalysis] =
    useState<ColorAnalysisResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for recent analysis display
  const [recentAnalysis, setRecentAnalysis] = useState<{
    id: number;
    name: string;
    image_url: string | null;
    dominant_colours: Array<{ rgb: number[]; hex: string; percentage: number }>;
    palette?: {
      jewelry?: Record<string, string>;
      hair_colors?: string[];
      eye_colors?: string[];
    };
    recommendations: Record<string, unknown>;
    recommended_colors: string[] | null;
    created_at: string;
    updated_at: string;
  } | null>(null);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const [selectedJewelryKeys, setSelectedJewelryKeys] = useState<string[]>([]);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for onboarding flow
  const [hasCompletedAnalysis, setHasCompletedAnalysis] = useState(false);
  const [aiInsightsModalVisible, setAiInsightsModalVisible] = useState(false);
  const routedInitialView = route?.params?.initialView as
    | 'upload'
    | 'results'
    | undefined;
  const routedInitialViewRequestId = route?.params?.initialViewRequestId as
    | number
    | undefined;
  const resolvedInitialView = initialView ?? routedInitialView ?? 'upload';
  const resolvedInitialViewRequestId =
    initialViewRequestId ?? routedInitialViewRequestId;
  const initialViewRequestTracker = useRef<number | null>(null);

  const resetJewelrySelection = useCallback(() => {
    setSelectedJewelryKeys([]);
  }, []);

  const syncJewelryKeysFromColors = useCallback(
    (colors: string[]) => {
      const jewelry = selectedPalette.jewelry ?? {};
      const keys: string[] = [];
      for (const c of colors) {
        const cUpper = c.toUpperCase();
        for (const [k, h] of Object.entries(jewelry)) {
          const canonical = (getJewelryMetalHex(k) ?? h).toUpperCase();
          if (canonical === cUpper && !keys.includes(k)) {
            keys.push(k);
          }
        }
      }
      return keys;
    },
    [selectedPalette.jewelry]
  );

  const applyDrapeSelection = useCallback(
    (nextColors: string[], options?: { analyzeColor?: string }) => {
      const capped = nextColors.slice(0, 2);
      setSelectedColors(capped);
      setSelectedColor(capped.length > 0 ? capped[capped.length - 1] : null);
      setWheelColors(
        capped.length === 0 ? selectedPalette.colors : capped
      );
      setSelectedJewelryKeys(syncJewelryKeysFromColors(capped));
      const toAnalyze =
        options?.analyzeColor ??
        (capped.length > 0 ? capped[capped.length - 1] : undefined);
      if (toAnalyze) {
        setColorAnalysis(skinUndertoneService.analyzeColor(toAnalyze));
      }
    },
    [selectedPalette.colors, selectedPalette.jewelry, syncJewelryKeysFromColors]
  );

  const handleJewelrySelection = useCallback(
    (key: string, hex: string) => {
      const canonicalHex = getJewelryMetalHex(key) ?? hex;
      const isSelected = selectedColors.some(
        (c) => c.toUpperCase() === canonicalHex.toUpperCase()
      );

      if (isSelected) {
        applyDrapeSelection(
          selectedColors.filter(
            (c) => c.toUpperCase() !== canonicalHex.toUpperCase()
          )
        );
        return;
      }

      let next: string[];
      if (selectedColors.length < 2) {
        next = [...selectedColors, canonicalHex];
      } else {
        next = [selectedColors[0], canonicalHex];
      }
      applyDrapeSelection(next, { analyzeColor: canonicalHex });
    },
    [selectedColors, applyDrapeSelection]
  );

  // Use the skin undertone hook
  const {
    undertone,
    colorPalette,
    setUndertone,
    reset: resetUndertone,
    analyzeUndertoneAdvanced,
  } = useSkinUndertone();

  const displayedPalettes = useMemo(() => {
    if (undertone) {
      const filtered = allPalettes.filter((p) => p.undertone === undertone);
      return filtered.length > 0 ? filtered : allPalettes;
    }
    return allPalettes;
  }, [undertone]);

  // Initialize face detection service
  useEffect(() => {
    const initializeFaceDetection = async () => {
      try {
        await faceDetectionService.initialize();
      } catch {
        // Face detection init failed
      }
    };
    initializeFaceDetection();
  }, []);

  // Enhanced face detection and cropping function with caching
  const detectAndCropFace = useCallback(async (imageUri: string) => {
    // Check cache first
    const cacheKey = imageUri;
    const cached = faceDetectionCache.current.get(cacheKey);
    if (cached) {
      setFaceDetectionResult(cached.result);
      return cached.croppedImage;
    }

    setIsDetectingFace(true);
    setFaceDetectionError(null);

    try {
      if (Platform.OS === 'web') {
        return new Promise<string>((resolve) => {
          const img = document.createElement('img');
          img.crossOrigin = 'anonymous';
          img.onload = async () => {
            try {
              // Create canvas to get image data
              const tempCanvas = document.createElement('canvas');
              const tempCtx = tempCanvas.getContext('2d');

              if (!tempCtx) {
                resolve(imageUri);
                return;
              }

              tempCanvas.width = img.width;
              tempCanvas.height = img.height;
              tempCtx.drawImage(img, 0, 0);

              const imageData = tempCtx.getImageData(
                0,
                0,
                img.width,
                img.height
              );

              // Detect face using the face detection service
              const faceResult =
                await faceDetectionService.detectFace(imageData);
              setFaceDetectionResult(faceResult);

              let cropParams;

              if (
                faceResult.detected &&
                faceResult.boundingBox &&
                faceResult.confidence > 0.5
              ) {
                // Use detected face for cropping
                const { x, y, width, height } = faceResult.boundingBox;

                // Add intelligent padding around the face (reduced for less background)
                const padding = Math.max(width, height) * 0.5;
                const paddedX = Math.max(0, x - padding);
                const paddedY = Math.max(0, y - padding);
                const paddedWidth = Math.min(
                  img.width - paddedX,
                  width + padding * 2
                );
                const paddedHeight = Math.min(
                  img.height - paddedY,
                  height + padding * 2
                );

                cropParams = {
                  x: paddedX,
                  y: paddedY,
                  width: paddedWidth,
                  height: paddedHeight,
                };
              } else {
                // Fallback to enhanced fixed cropping (centered & tighter)
                const faceWidth = img.width * 0.5;
                const faceHeight = img.height * 0.6;
                const faceX = (img.width - faceWidth) / 2;
                const faceY = img.height * 0.15;

                cropParams = {
                  x: faceX,
                  y: faceY,
                  width: faceWidth,
                  height: faceHeight,
                };
              }

              // Create final cropped image
              const finalCanvas = document.createElement('canvas');
              const finalCtx = finalCanvas.getContext('2d');

              if (!finalCtx) {
                resolve(imageUri);
                return;
              }

              const cropWidth = Math.max(1, cropParams.width);
              const cropHeight = Math.max(1, cropParams.height);

              finalCanvas.width = cropWidth;
              finalCanvas.height = cropHeight;

              // Apply oval mask so the preview mirrors the capture guide
              finalCtx.save();
              finalCtx.beginPath();
              finalCtx.ellipse(
                cropWidth / 2,
                cropHeight / 2,
                cropWidth / 2,
                cropHeight / 2,
                0,
                0,
                Math.PI * 2
              );
              finalCtx.closePath();
              finalCtx.clip();

              finalCtx.drawImage(
                img,
                cropParams.x,
                cropParams.y,
                cropWidth,
                cropHeight,
                0,
                0,
                cropWidth,
                cropHeight
              );
              finalCtx.restore();

              const croppedDataUrl = finalCanvas.toDataURL('image/png', 0.95);

              // Cache the result
              faceDetectionCache.current.set(cacheKey, {
                result: faceResult,
                croppedImage: croppedDataUrl,
              });

              resolve(croppedDataUrl);
            } catch {
              setFaceDetectionError('Face detection failed, using fallback');
              resolve(imageUri);
            }
          };
          img.onerror = () => {
            setFaceDetectionError('Failed to load image');
            resolve(imageUri);
          };
          img.src = imageUri;
        });
      } else {
        // For mobile, return original image with transform
        return imageUri;
      }
    } catch {
      setFaceDetectionError('Face detection failed');
      return imageUri;
    } finally {
      setIsDetectingFace(false);
    }
  }, []);

  // Automatically crop image to focus on face when image is set
  const autoCropImageToFace = useCallback(
    async (
      imageUri: string,
      options?: {
        replaceFaceImage?: boolean;
      }
    ) => {
      const croppedImage = await detectAndCropFace(imageUri);
      setCroppedFaceImage(croppedImage);

      if (options?.replaceFaceImage) {
        setFaceImage(croppedImage);
      }

      return croppedImage;
    },
    [detectAndCropFace]
  );

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 500,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true,
      }),
    ]).start();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fadeAnim, slideAnim, scaleAnim are stable refs from useState
  }, []);

  // Track virtual drape usage for badge (skip when public preview - no auth)
  const trackVirtualDrapeUsage = useCallback(async () => {
    if (isPublicPreview) return;
    try {
      const updatedUser = await usersAPI.trackBadgeUsage('virtual_drape');
      if (updatedUser) {
        queryClient.invalidateQueries({ queryKey: authKeys.user() });
      }
    } catch {
      // Track usage failed
    }
  }, [isPublicPreview, queryClient]);

  // Function to fetch the most recent image, recommended color, and dominant color
  // Uses token interceptor to automatically handle token refresh
  const fetchRecentAnalysisData = useCallback(async () => {
    // Track button click
    trackButtonClick(
      'Colour Analysis Results',
      'virtual-drape-screen-upload-section'
    );

    trackVirtualDrapeUsage();

    let progressInterval: NodeJS.Timeout | undefined;

    try {
      setIsLoadingRecent(true);
      setIsAnalyzing(true);
      setAnalysisProgress(0);
      setHasCompletedAnalysis(false);

      // Simulate progress for better UX with slower timing
      progressInterval = setInterval(() => {
        setAnalysisProgress((prev) => {
          if (prev >= 85) {
            clearInterval(progressInterval);
            return 85;
          }
          return prev + Math.random() * 8 + 2; // Slower, more gradual progress
        });
      }, 300); // Slower interval

      const analysis = await getRecentAnalysis();
      setRecentAnalysis(analysis);

      // If we have recent analysis data, use it to populate the virtual drape
      if (analysis.image_url) {
        setFaceImage(analysis.image_url);
        setImageSourceType('analysis');

        // Automatically crop image to focus on face
        await autoCropImageToFace(analysis.image_url, {
          replaceFaceImage: true,
        });

        // Set the recommended colors from the analysis
        if (
          analysis.recommended_colors &&
          analysis.recommended_colors.length > 0
        ) {
          const dbUndertone = parseUndertoneFromStorage(
            String(analysis.recommendations?.undertone ?? '')
          );

          const recommendedPalette: PaletteType = {
            id: 'recommended-analysis',
            undertone: dbUndertone,
            name: 'Recommended Colors',
            description: 'Colors recommended from your analysis',
            colors: analysis.recommended_colors,
            jewelry: analysis.palette?.jewelry,
          };

          setSelectedPalette(recommendedPalette);
          resetJewelrySelection();
          setWheelColors(analysis.recommended_colors);
          setSelectedColor(analysis.recommended_colors[0]);
          setIsShowingRecommendedColors(false);
        }

        // Use the undertone from recommendations for scoring analysis
        if (analysis.recommendations?.undertone) {
          const dbUndertone = parseUndertoneFromStorage(
            String(analysis.recommendations.undertone)
          );

          // Set the undertone in the hook state so the scoring display works
          setUndertone(dbUndertone);

          // Also set it in the service for color analysis
          const mockSkinToneData = {
            undertone: dbUndertone,
            skinToneCategory:
              analysis.recommendations?.skin_tone_category || 'Unknown',
            confidence: analysis.recommendations?.confidence || 0.8,
            dominantColors:
              analysis.dominant_colours?.map((color) => ({
                r: color.rgb[0],
                g: color.rgb[1],
                b: color.rgb[2],
                hex: color.hex,
              })) || [],
          };

          // Set the skin tone data in the service for color analysis
          (skinUndertoneService as Record<string, unknown>).skinToneData =
            mockSkinToneData;
        }
      }

      // Wait for minimum duration to show progress
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Complete the progress
      clearInterval(progressInterval);
      setAnalysisProgress(100);
      setHasCompletedAnalysis(true);

      // Wait a bit more before hiding the progress
      await new Promise((resolve) => setTimeout(resolve, 800));
      setAnalysisProgress(0);
    } catch (error: unknown) {
      // Clear progress interval
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      setAnalysisProgress(0);

      // Show user-friendly error message
      const errorMessage =
        (error instanceof Error ? error.message : null) ||
        'Failed to load colour analysis';

      if (
        errorMessage.includes('not authenticated') ||
        errorMessage.includes('User not authenticated')
      ) {
        Alert.alert(
          'Authentication Required',
          'Please log in to view your colour analysis results.',
          [{ text: 'OK', style: 'default' }]
        );
      } else if (errorMessage.includes('No colour profiles found')) {
        Alert.alert(
          'No Analysis Found',
          "You haven't completed any colour analysis yet. Please complete a colour analysis first.",
          [
            {
              text: 'OK',
              style: 'default',
            },
            {
              text: 'Go to Analysis',
              onPress: () =>
                navigation.navigate('MainTabs', {
                  initialTab: 'ColourAnalysis',
                }),
            },
          ]
        );
      } else {
        Alert.alert(
          'Error',
          'Failed to load your colour analysis. Please try again later.',
          [{ text: 'OK' }]
        );
      }
    } finally {
      setIsLoadingRecent(false);
      setIsAnalyzing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackVirtualDrapeUsage is fire-and-forget; autoCropImageToFace not used in this flow
  }, [resetJewelrySelection, setUndertone, navigation]);

  useEffect(() => {
    const shouldAutoLoadResults =
      resolvedInitialView === 'results' &&
      resolvedInitialViewRequestId !== undefined;

    if (
      shouldAutoLoadResults &&
      resolvedInitialViewRequestId !== initialViewRequestTracker.current
    ) {
      initialViewRequestTracker.current = resolvedInitialViewRequestId;
      fetchRecentAnalysisData();
    } else if (!shouldAutoLoadResults) {
      initialViewRequestTracker.current = null;
    }
  }, [
    resolvedInitialView,
    resolvedInitialViewRequestId,
    fetchRecentAnalysisData,
  ]);

  // Explicit AI call path used by upload/drag/camera flows.
  // This mirrors color-analysis behavior: analyze image -> call AI -> apply palette.
  const applyAIPaletteForImage = useCallback(
    async (
      imageUri: string,
      detectedSkinTone?: {
        category: string;
        confidence: number;
        color: string;
      } | null
    ) => {
      if (!ENABLE_AI_FEATURES) {
        setIsAIPaletteLoading(false);
        return;
      }
      if (!imageUri) return;
      if (isShowingRecommendedColors) return; // don't override DB-recommended palette flow
      if (aiPaletteImageRef.current === imageUri) return; // already attempted for this image

      const skinToneData = skinUndertoneService.getSkinToneData();
      const effectiveUndertone = skinToneData?.undertone || undertone;
      if (!effectiveUndertone) return;

      aiPaletteImageRef.current = imageUri;
      setIsAIPaletteLoading(true);
      try {
        const aiResult = await getAIColorRecommendations({
          undertone: legacyUndertoneForApi(effectiveUndertone),
          skinToneCategory:
            detectedSkinTone?.category ||
            skinTone?.category ||
            skinToneData?.skinToneCategory ||
            'Unknown',
          skinToneHex:
            detectedSkinTone?.color ||
            skinTone?.color ||
            skinToneData?.dominantColors?.[0]?.hex ||
            null,
          dominantColors:
            skinToneData?.dominantColors?.map((c) => ({ hex: c.hex })) ?? [],
        });

        const aiPalette: PaletteType = {
          id: 'recommended-ai',
          undertone: effectiveUndertone,
          name: aiResult.paletteName || 'AI Recommended Colors',
          description:
            aiResult.description || 'Colors recommended based on your analysis',
          colors: aiResult.recommendedColors,
          avoidColors: aiResult.avoidColors,
        };

        setSelectedPalette(aiPalette);
        resetJewelrySelection();
        setWheelColors(aiPalette.colors);
        setSelectedColor(aiPalette.colors[0] || null);
        setSelectedColors([]);
        setIsShowingRecommendedColors(true);
      } catch {
        // Keep existing static palette on AI failure.
      } finally {
        setIsAIPaletteLoading(false);
      }
    },
    [isShowingRecommendedColors, undertone, skinTone, resetJewelrySelection]
  );

  // Sync color wheel when analysis returns a recommended palette
  useEffect(() => {
    if (!colorPalette) return;
    const timer = setTimeout(() => {
      const newPalette = {
        ...colorPalette,
        id: 'recommended',
      };
      setSelectedPalette(newPalette);
      resetJewelrySelection();
      setWheelColors(newPalette.colors);
      setSelectedColor(newPalette.colors[0]);
      setSelectedColors([]);
    }, 0);
    return () => clearTimeout(timer);
  }, [colorPalette, resetJewelrySelection]);

  const shareCardRef = useRef<ViewShot>(null);

  const analyzeImageUndertone = useCallback(
    async (imageUri: string) => {
      try {
        // Analysis state should already be set by the calling function

        // Simulate progress for better UX with slower, more realistic timing
        const progressInterval = setInterval(() => {
          setAnalysisProgress((prev) => {
            if (prev >= 85) {
              clearInterval(progressInterval);
              return 85;
            }
            return prev + Math.random() * 8 + 2; // Slower, more gradual progress
          });
        }, 300); // Slower interval

        // Start the actual analysis
        const analysisPromise = (async () => {
          // Checks if the current platform is web
          if (typeof document !== 'undefined') {
            // Create a canvas to get ImageData from the image
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new (
              window as typeof globalThis & {
                Image: new () => HTMLImageElement;
              }
            ).Image() as HTMLImageElement;

            await new Promise<void>((resolve, reject) => {
              img.onload = () => resolve();
              img.onerror = () => reject(new Error('Failed to load image'));
              img.src = imageUri;
            });

            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);

            const imageData = ctx?.getImageData(
              0,
              0,
              canvas.width,
              canvas.height
            );
            if (imageData) {
              const { region, faceResult } =
                await resolveFaceAnalysisForUndertone(imageData);
              setFaceDetectionResult(faceResult);

              await analyzeUndertoneAdvanced(imageData, region);

              // Also analyze the image for color analysis
              await skinUndertoneService.analyzeImage(imageData);
            }
          } else {
            // In React Native, use a simpler approach
            // Skip undertone analysis on native platforms for now
          }
        })();

        // Wait for both progress and analysis to complete
        await Promise.all([
          analysisPromise,
          new Promise((resolve) => setTimeout(resolve, 2000)), // Minimum 2 seconds
        ]);

        // Complete the progress
        clearInterval(progressInterval);
        setAnalysisProgress(100);
        setHasCompletedAnalysis(true);

        // Wait a bit more before hiding the progress
        await new Promise((resolve) => setTimeout(resolve, 800));

        setAnalysisProgress(0);
      } catch {
        setAnalysisProgress(0);
      } finally {
        setIsAnalyzing(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- autoCropImageToFace, trackVirtualDrapeUsage are stable; adding causes unnecessary reruns
    [analyzeUndertoneAdvanced]
  );

  const pickImage = useCallback(async () => {
    trackVirtualDrapeUsage();

    resetUndertone();

    // Request permission
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();

    if (status !== 'granted') {
      alert('We need camera roll permissions to upload a selfie.');
      return;
    }

    // Pick image
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled && result.assets && result.assets.length > 0) {
      // Set analysis state immediately to show progress
      setIsAnalyzing(true);
      setAnalysisProgress(0);
      setHasCompletedAnalysis(false);
      setIsAIPaletteLoading(false);
      aiPaletteImageRef.current = null;

      setFaceImage(result.assets[0].uri);
      setImageSourceType('upload');
      setIsShowingRecommendedColors(false);

      // Automatically crop image to focus on face
      await autoCropImageToFace(result.assets[0].uri, {
        replaceFaceImage: true,
      });

      // Analyze skin undertone from the selected image
      await analyzeImageUndertone(result.assets[0].uri);
      await applyAIPaletteForImage(result.assets[0].uri);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackVirtualDrapeUsage fire-and-forget
  }, [
    resetUndertone,
    analyzeImageUndertone,
    autoCropImageToFace,
    applyAIPaletteForImage,
  ]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      const imageFile = files.find(
        (file: File) => file.type && file.type.startsWith('image/')
      );

      if (!imageFile) {
        alert('Please drop an image file');
        return;
      }

      try {
        trackVirtualDrapeUsage();

        // Reset undertone analysis
        resetUndertone();

        // Convert file to data URL
        const reader = new FileReader();
        reader.onload = async (event: ProgressEvent<FileReader>) => {
          const imageUri = event.target?.result as string;
          if (imageUri) {
            // Set analysis state immediately to show progress
            setIsAnalyzing(true);
            setAnalysisProgress(0);
            setHasCompletedAnalysis(false);
            setIsAIPaletteLoading(false);
            aiPaletteImageRef.current = null;

            setFaceImage(imageUri);
            setImageSourceType('drag');
            setIsShowingRecommendedColors(false);

            // Automatically crop image to focus on face
            await autoCropImageToFace(imageUri, {
              replaceFaceImage: true,
            });

            // Analyze skin undertone from the dropped image
            await analyzeImageUndertone(imageUri);
            await applyAIPaletteForImage(imageUri);
          }
        };
        if (imageFile instanceof Blob) {
          reader.readAsDataURL(imageFile);
        } else {
          throw new Error('Dropped file is not a valid Blob.');
        }
      } catch {
        alert('Failed to process dropped image. Please try again.');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackVirtualDrapeUsage is fire-and-forget
    [
      resetUndertone,
      analyzeImageUndertone,
      autoCropImageToFace,
      applyAIPaletteForImage,
    ]
  );

  const takeSelfie = useCallback(() => {
    trackButtonClick(
      'Real-time Testing',
      'virtual-drape-screen-upload-section'
    );

    setIsCameraMode(true);
  }, []);

  const goToPremium = useCallback(() => {
    navigation.navigate('PremiumPaywall');
  }, [navigation]);

  const _createShareableCard = useCallback(() => {
    if (!faceImage || !selectedColor) {
      Alert.alert(
        "Can't Create Card",
        'Please select an image and a colour first.'
      );
      return;
    }

    setIsCreatingCard(true);
  }, [faceImage, selectedColor]);

  const shareCard = useCallback(async () => {
    if (!shareCardRef.current) return;

    try {
      setShareLoading(true);

      // Capture the view as an image using optional chaining
      const captureFunction = shareCardRef.current.capture;
      if (!captureFunction) {
        throw new Error('Capture function not available');
      }

      const uri = await captureFunction();

      // Save to media library if on iOS
      if (Platform.OS === 'ios') {
        const { status } = await MediaLibrary.requestPermissionsAsync();

        if (status === 'granted') {
          await MediaLibrary.saveToLibraryAsync(uri);
          Alert.alert('Success', 'Card saved to your photos!');
        }
      }

      // Share the image
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri, {
          mimeType: 'image/jpeg',
          dialogTitle: 'Share Your Colour Card',
          UTI: 'public.jpeg',
        });
      } else {
        Alert.alert(
          'Sharing not available',
          'Sharing is not available on your device'
        );
      }
    } catch {
      Alert.alert('Error', 'Failed to share your colour card.');
    } finally {
      setShareLoading(false);
      setIsCreatingCard(false);
    }
  }, []);

  const cancelShareCard = useCallback(() => {
    setIsCreatingCard(false);
  }, []);

  const _renderPaletteButton = useCallback(
    (palette: PaletteType) => (
      <LinearGradient
        key={palette.id}
        colors={
          selectedPalette.id === palette.id
            ? [colors.primary, colors.supportText, colors.success]
            : [colors.border, colors.text + '80']
        }
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.paletteButton}
      >
        <TouchableOpacity
          style={styles.paletteButtonTouchable}
          onPress={() => {
            setSelectedPalette(palette);
            resetJewelrySelection();
            setSelectedColor(palette.colors[0]);
            setWheelColors(palette.colors);
            setSelectedColors([]);
          }}
        >
          <Text
            style={[
              styles.paletteButtonText,
              { color: colors.cardText },
              selectedPalette.id === palette.id && { color: colors.background },
            ]}
          >
            {palette.name}
          </Text>
        </TouchableOpacity>
      </LinearGradient>
    ),
    [resetJewelrySelection, selectedPalette.id, colors]
  );

  const resetImage = useCallback(() => {
    trackButtonClick('Start Over', 'virtual-drape-screen-results-section');

    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setFaceImage(null);
    setSelectedColor(null);
    setSelectedColors([]);
    setSkinTone(null);
    setColorAnalysis(null);
    setCroppedFaceImage(null);
    setFaceDetectionResult(null);
    setFaceDetectionError(null);
    setIsDetectingFace(false);
    setAnalysisProgress(0);
    setHasCompletedAnalysis(false);
    setImageSourceType(null);
    setIsAIPaletteLoading(false);
    setAiInsightsModalVisible(false);
    aiPaletteImageRef.current = null;
    // Clear face detection cache
    faceDetectionCache.current.clear();
    resetUndertone();
    skinUndertoneService.reset();
  }, [resetUndertone]);

  // Get undertone color for chip
  const _getUndertoneColor = useCallback((undertoneType: string) => {
    switch (undertoneType) {
      case 'spring':
        return palette.lightOrange;
      case 'summer':
        return palette.vividBlue;
      case 'autumn':
        return palette.vividOrange;
      case 'winter':
        return palette.darkModerateViolet;
      default:
        return palette.mediumGray3;
    }
  }, []);

  const _renderPremiumBanner = () => {
    if (isPremium) return null;

    return (
      <Animated.View
        style={[
          styles.premiumBanner,
          {
            backgroundColor: colors.card,
            borderColor: colors.border,
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <TouchableOpacity
          style={styles.premiumBannerContent}
          onPress={goToPremium}
          activeOpacity={0.8}
        >
          <View
            style={[
              styles.premiumIconContainer,
              { backgroundColor: colors.primary },
            ]}
          >
            <Text style={[styles.premiumIcon, { color: colors.background }]}>
              ✨
            </Text>
          </View>
          <View style={styles.premiumTextContainer}>
            <Text style={[styles.premiumBannerTitle, { color: colors.text }]}>
              Upgrade to Premium
            </Text>
            <Text
              style={[styles.premiumBannerText, { color: colors.secondary }]}
            >
              Get access to Smart Wardrobe & Outfit Planning
            </Text>
          </View>
          <Text style={[styles.premiumBannerArrow, { color: colors.primary }]}>
            ›
          </Text>
        </TouchableOpacity>
      </Animated.View>
    );
  };

  const renderUploadSection = () => (
    <Animated.View
      style={[
        styles.uploadSection,
        {
          opacity: fadeAnim,
          transform: [{ scale: scaleAnim }],
          borderColor: colors.borderLight,
          paddingHorizontal: isMobile ? 20 : 20,
          paddingVertical: isMobile ? 20 : 20,
          ...(Platform.OS === 'web'
            ? {
                boxShadow: `0px 4px 12px 0px ${colors.cardText}1A`,
              }
            : { shadowColor: colors.cardText }),
        },
      ]}
    >
      <Text style={[styles.title, { color: colors.text }]}>
        Test Colors Against Your Face
      </Text>
      <Text style={[styles.subtitle, { color: colors.secondary }]}>
        Upload a selfie or use real-time camera testing to compare how different
        colors look next to your face
      </Text>

      {Platform.OS === 'web' ? (
        <WebUploadContainer
          colors={colors}
          isDragOver={isDragOver}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onPickImage={pickImage}
          onTakeSelfie={takeSelfie}
          isAnalyzing={isAnalyzing}
          // isPremium={isPremium}
          isLoadingRecent={isLoadingRecent}
          onLoadRecentAnalysis={fetchRecentAnalysisData}
          hideColorAnalysisButton={isPublicPreview}
        />
      ) : (
        <View
          style={[
            styles.uploadContainer,
            {
              borderColor: colors.theme_color,
              width: isMobile ? '100%' : '75%',
              padding: isMobile ? 20 : 30,
            },
          ]}
        >
          <TouchableOpacity
            style={styles.uploadIconContainer}
            onPress={pickImage}
            activeOpacity={0.8}
          >
            <View style={styles.uploadIcon}>
              <Ionicons name="images" size={32} color={colors.primary} />
            </View>
            <Text style={[styles.uploadTitle, { color: colors.text }]}>
              Upload an Image
            </Text>
            <Text style={[styles.uploadText, { color: colors.secondary }]}>
              Choose from Gallery
            </Text>
          </TouchableOpacity>

          <Button
            title="Real-time Testing"
            onPress={takeSelfie}
            icon={<Ionicons name="camera" size={20} color={colors.card} />}
            style={styles.cameraButton}
            textStyle={{
              ...styles.cameraText,
              ...(isMobile && { fontSize: 14 }),
            }}
            variant="primary"
            trackClick={true}
            trackingLocation="virtual-drape-test-screen"
            trackingData={{
              button_name: 'Real-time Testing',
              button_type: 'real_time_testing',
              screen: 'virtual_drape_test',
            }}
          />

          {isAnalyzing && (
            <View style={styles.analyzingIndicator}>
              <ActivityIndicator size="small" color={colors.primary} />
              <Text style={[styles.analyzingText, { color: colors.secondary }]}>
                Building Your Palette
              </Text>
            </View>
          )}
          {!isPublicPreview && (
            <Button
              title={
                isLoadingRecent
                  ? 'Loading Recent Analysis...'
                  : 'Colour Analysis Results'
              }
              onPress={fetchRecentAnalysisData}
              disabled={isLoadingRecent}
              icon={
                <Ionicons
                  name="color-palette"
                  size={20}
                  color={colors.primary}
                />
              }
              style={styles.cameraButton}
              textStyle={{
                ...styles.cameraText,
                ...(isMobile && { fontSize: 14 }),
              }}
              variant="outline"
              hoverColor={colors.authBackground}
              trackClick={true}
              trackingLocation="virtual-drape-test-screen"
              trackingData={{
                button_name: 'Colour Analysis Results',
                button_type: 'colour_analysis_results',
                screen: 'virtual_drape_test',
              }}
            />
          )}
        </View>
      )}
    </Animated.View>
  );

  const renderDrapeTestSection = () => {
    const getCroppedImageTransform = () => {
      if (!croppedFaceImage) {
        return { scale: 1, marginTop: 0 };
      }

      switch (imageSourceType) {
        case 'upload':
          return { scale: 1.32, marginTop: -IMAGE_SIZE * 0.12 };
        case 'analysis':
          return { scale: 1.28, marginTop: -IMAGE_SIZE * 0.11 };
        case 'drag':
          return { scale: 1.24, marginTop: -IMAGE_SIZE * 0.1 };
        default:
          return { scale: 1.18, marginTop: -IMAGE_SIZE * 0.08 };
      }
    };

    const { scale: croppedScale, marginTop: croppedMarginTop } =
      getCroppedImageTransform();

    return (
      <Animated.View
        style={[
          styles.drapeTestSection,
          {
            opacity: fadeAnim,
            transform: [{ scale: scaleAnim }],
          },
        ]}
      >
        <View
          style={[
            styles.imagePreviewContainer,
            {
              marginHorizontal: isMobile ? 0 : -150,
              backgroundColor: colors.card,
              shadowColor: colors.cardText,
              borderColor: colors.borderLight,
            },
          ]}
        >
          <View
            style={[
              styles.imagePreviewResults,
              {
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 12 : 16,
              },
            ]}
          >
            <View style={[styles.imagePreviewCard]}>
              <View
                style={[
                  styles.drapeContainer,
                  { width: WHEEL_SIZE, height: WHEEL_SIZE },
                ]}
              >
                {/* Face Detection Status */}
                {isDetectingFace && (
                  <View style={styles.faceDetectionStatus}>
                    <ActivityIndicator size="small" color={colors.primary} />
                    <Text
                      style={[styles.faceDetectionText, { color: colors.text }]}
                    >
                      Building Your Palette
                    </Text>
                  </View>
                )}

                {faceDetectionError && (
                  <View style={styles.faceDetectionError}>
                    <Ionicons
                      name="warning"
                      size={16}
                      color={colors.errorText}
                    />
                    <Text
                      style={[
                        styles.faceDetectionErrorText,
                        { color: colors.errorText },
                      ]}
                    >
                      {faceDetectionError}
                    </Text>
                  </View>
                )}

                <Image
                  source={{
                    uri: croppedFaceImage || faceImage!,
                  }}
                  style={{
                    width: IMAGE_SIZE,
                    height: IMAGE_SIZE,
                    borderRadius: IMAGE_SIZE / 2,
                    resizeMode: 'cover',
                    ...(croppedFaceImage
                      ? {
                          transform: [{ scale: croppedScale }],
                          marginTop: croppedMarginTop,
                          marginLeft: 0,
                        }
                      : {
                          transform: [
                            {
                              scale:
                                faceDetectionResult?.detected &&
                                faceDetectionResult.confidence > 0.7
                                  ? 1.2
                                  : 1.3,
                            },
                          ],
                          marginTop:
                            faceDetectionResult?.detected &&
                            faceDetectionResult.confidence > 0.7
                              ? -IMAGE_SIZE * 0.05
                              : -IMAGE_SIZE * 0.1,
                          marginLeft: -IMAGE_SIZE * 0.05,
                        }),
                  }}
                />
                <View style={styles.wheelOverlay}>
                  <DynamicColorWheel
                    colors={wheelColors}
                    onColorSelect={(color, isSecondColor) => {
                      if (isSecondColor === false) {
                        // Deselecting a color
                        setSelectedColors((prev) => {
                          const newColors = prev.filter((c) => c !== color);
                          if (newColors.length === 1) {
                            setSelectedColor(newColors[0]);
                            setWheelColors([newColors[0]]);
                          } else {
                            setSelectedColor(null);
                            setWheelColors(selectedPalette.colors);
                          }
                          return newColors;
                        });
                      } else if (isSecondColor === true) {
                        // Selecting second color
                        setSelectedColors((prev) => {
                          const newColors = [prev[0], color];
                          setSelectedColor(color);
                          setWheelColors(newColors);
                          return newColors;
                        });
                      } else {
                        // Selecting first color
                        setSelectedColors([color]);
                        setSelectedColor(color);
                        setWheelColors([color]);
                      }

                      // if (isPremium) {
                      //   // Premium user logic for 2-color selection
                      //   if (isSecondColor === false) {
                      //     // Deselecting a color
                      //     setSelectedColors((prev) => {
                      //       const newColors = prev.filter((c) => c !== color);
                      //       if (newColors.length === 1) {
                      //         setSelectedColor(newColors[0]);
                      //         setWheelColors([newColors[0]]);
                      //       } else {
                      //         setSelectedColor(null);
                      //         setWheelColors(selectedPalette.colors);
                      //       }
                      //       return newColors;
                      //     });
                      //   } else if (isSecondColor === true) {
                      //     // Selecting second color
                      //     setSelectedColors((prev) => {
                      //       const newColors = [prev[0], color];
                      //       setSelectedColor(color);
                      //       setWheelColors(newColors);
                      //       return newColors;
                      //     });
                      //   } else {
                      //     // Selecting first color
                      //     setSelectedColors([color]);
                      //     setSelectedColor(color);
                      //     setWheelColors([color]);
                      //   }
                      // } else {
                      //   // Free user logic - single color selection
                      //   if (selectedColor === color && wheelColors.length === 1) {
                      //     setWheelColors(selectedPalette.colors);
                      //   } else {
                      //     setSelectedColor(color);
                      //     setWheelColors([color]);
                      //   }
                      // }

                      // Analyze the selected color using the functional service
                      const analysis = skinUndertoneService.analyzeColor(color);
                      setColorAnalysis(analysis);
                      setUserFeedback(analysis.feedback);

                      // Show feedback briefly
                      setTimeout(() => {
                        setUserFeedback('');
                      }, 5000);
                    }}
                    size={WHEEL_SIZE}
                    innerRadiusRatioX={IMAGE_SIZE / WHEEL_SIZE}
                    innerRadiusRatioY={IMAGE_SIZE / WHEEL_SIZE}
                    // isPremium={isPremium}
                    selectedColors={selectedColors}
                  />
                </View>
              </View>

              {!isPublicPreview && (
                <View
                  style={[
                    styles.imageActions,
                    isMobile && styles.imageActionsMobile,
                  ]}
                >
                  <Button
                    title="Start Over"
                    onPress={resetImage}
                    style={styles.changeImageButton}
                    variant="outline"
                    icon={
                      <Ionicons
                        name="refresh"
                        size={16}
                        color={colors.primary}
                      />
                    }
                    hoverColor={colors.authBackground}
                    trackClick={true}
                    trackingLocation="virtual-drape-test-screen"
                    trackingData={{
                      button_name: 'Start Over Virtual Drape Test Screen',
                      button_type: 'start_over_virtual_drape_test_screen',
                      screen: 'virtual_drape_test',
                    }}
                  />
                </View>
              )}
            </View>

            {(() => {
              const containerStyle = [
                styles.analysisCardsContainer,
                { width: isMobile ? '100%' : '40%' },
              ];
              const content = (
                <>
                  {/* Color Palettes section */}
                  {!isShowingRecommendedColors && (
                    <View style={[styles.palettesSection, { marginTop: isMobile ? -100 : 'auto'}]}>
                      <Text
                        style={[styles.sectionTitle, { color: colors.text }]}
                      >
                        Colour Palettes
                      </Text>
                      <View
                        style={[
                          styles.paletteButtons,
                          { gap: isMobile ? 4 : 8 },
                        ]}
                      >
                        {displayedPalettes.slice(0, 3).map((palette) => (
                          <LinearGradient
                            key={palette.id}
                            colors={
                              selectedPalette.id === palette.id
                                ? [
                                    colors.primary,
                                    colors.theme_color,
                                    colors.fabGradientPurple,
                                    colors.authBackground + 'CC',
                                  ]
                                : [
                                    colors.weatherGradient2,
                                    colors.borderLight,
                                    colors.border + '80',
                                  ]
                            }
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1.5, y: 1 }}
                            style={[
                              styles.paletteButton,
                              isMobile && { flex: 1, marginRight: 0 },
                            ]}
                          >
                            <TouchableOpacity
                              style={styles.paletteButtonTouchable}
                              onPress={() => {
                                setSelectedPalette(palette);
                                resetJewelrySelection();
                                setSelectedColor(palette.colors[0]);
                                setWheelColors(palette.colors);
                                setSelectedColors([]);
                              }}
                            >
                              <Text
                                style={[
                                  styles.paletteButtonText,
                                  {
                                    color:
                                      selectedPalette.id === palette.id
                                        ? colors.background
                                        : colors.text,
                                    fontSize: isMobile ? 12 : 14,
                                  },
                                ]}
                              >
                                {palette.name}
                              </Text>
                            </TouchableOpacity>
                          </LinearGradient>
                        ))}
                      </View>
                    </View>
                  )}

                  {/* Selected palette section */}
                  <View
                    style={[
                      styles.selectedPaletteSection,
                      isMobile &&
                        isShowingRecommendedColors &&
                        styles.selectedPaletteSectionRecommendedMobile,
                    ]}
                  >
                    <Text
                      style={[
                        styles.sectionTitle,
                        { color: colors.text },
                        isMobile &&
                          isShowingRecommendedColors &&
                          styles.sectionTitleRecommendedMobile,
                      ]}
                    >
                      {selectedPalette.name}
                    </Text>
                    <Text
                      style={[
                        styles.colorInstructions,
                        { color: colors.secondary },
                      ]}
                    >
                      Tap a colour to see how it looks against your face
                    </Text>
                    <View style={styles.colorSwatches}>
                      {selectedPalette.colors.map(
                        (color: string, index: number) => (
                          <TouchableOpacity
                            key={index.toString()}
                            style={[
                              styles.colorSwatch,
                              {
                                backgroundColor: color,
                                borderColor:
                                  selectedColor === color ||
                                  selectedColors.includes(color)
                                    ? colors.primary
                                    : 'transparent',
                                borderWidth:
                                  selectedColor === color ||
                                  selectedColors.includes(color)
                                    ? 3
                                    : 0,
                              },
                            ]}
                            onPress={() => {
                              let next: string[];
                              if (selectedColors.includes(color)) {
                                next = selectedColors.filter((c) => c !== color);
                              } else if (selectedColors.length < 2) {
                                next = [...selectedColors, color];
                              } else {
                                next = [selectedColors[0], color];
                              }
                              applyDrapeSelection(next, { analyzeColor: color });
                            }}
                          />
                        )
                      )}
                    </View>
                  </View>

                  {(() => {
                    const jewelryOrder = ['accent1', 'accent2', 'crystal'];
                    const jewelryEntries = Object.entries(
                      selectedPalette.jewelry ?? {}
                    ).sort(([a], [b]) => {
                      const ia = jewelryOrder.indexOf(a);
                      const ib = jewelryOrder.indexOf(b);
                      if (ia !== -1 || ib !== -1) {
                        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
                      }
                      return a.localeCompare(b);
                    });
                    if (jewelryEntries.length === 0) return null;
                    return (
                      <View style={{ marginBottom: isMobile ? 120 : 8 }}>
                        <Text
                          style={[
                            styles.gildedGlowTitle,
                            { color: colors.primary },
                          ]}
                        >
                          Jewelry
                        </Text>
                        <View style={styles.gildedGlowOptions}>
                          {jewelryEntries.map(([key, hex]) => (
                            <TouchableOpacity
                              key={key}
                              style={[
                                styles.gildedGlowOption,
                                {
                                  borderColor: colors.primary,
                                  backgroundColor:
                                    selectedJewelryKeys.includes(key)
                                      ? colors.primary + '20'
                                      : 'transparent',
                                },
                              ]}
                              onPress={() => handleJewelrySelection(key, hex)}
                            >
                              <JewelryMetalSwatch
                                jewelryKey={key}
                                hex={hex}
                                style={styles.gildedGlowSwatch}
                                borderColor={colors.border}
                              />
                              <Text
                                style={[
                                  styles.gildedGlowText,
                                  { color: colors.primary },
                                ]}
                              >
                                {jewelryLabelForKey(key)}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>
                    );
                  })()}
                </>
              );
              return isPublicPreview ? (
                <BlurredLockOverlay
                  message="Sign up to unlock the drape test!"
                  blurAmount={5}
                  overlayOpacity={0.55}
                  style={containerStyle}
                >
                  {content}
                </BlurredLockOverlay>
              ) : (
                <View style={containerStyle}>{content}</View>
              );
            })()}
          </View>
        </View>

        {/* <View style={{ marginBottom: isMobile ? 100 : 8 }}>
        <Text style={[styles.sectionTitle, { color: colors.text }]}>
          Create Shareable Card
        </Text>

        {selectedColor ? (
          <View
            style={[
              styles.feedbackCard,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
          >
            <Text style={[styles.feedbackTitle, { color: colors.text }]}>
              Ready to share your color analysis?
            </Text>

            <Text style={[styles.feedbackText, { color: colors.secondary }]}>
              Create a beautiful shareable card with your color analysis
              results.
            </Text>

            <View style={styles.buttonContainer}>
              <Button
                title='Create Shareable Color Card'
                onPress={_createShareableCard}
                style={styles.shareActionButton}
                textStyle={{
                  ...styles.shareActionButtonText,
                  color: colors.background,
                }}
                variant='primary'
                trackClick={true}
                trackingLocation="virtual-drape-test-screen"
                trackingData={{
                  button_name: 'Create Shareable Color Card',
                  button_type: 'create_shareable_color_card',
                  screen: 'virtual_drape_test',
                }}
              />
            </View>
          </View>
        ) : (
          <View
            style={[
              styles.feedbackCard,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
          >
            <Text style={[styles.feedbackText, { color: colors.secondary }]}>
              Select a color above to create a shareable card
            </Text>
          </View>
        )}
      </View> */}

        {/* {!isPremium && (
        <View
          style={[
            styles.upgradeCTA,
            { backgroundColor: colors.card, borderColor: colors.border },
          ]}
        >
          <Text style={[styles.upgradeTitle, { color: colors.primary }]}>
            Want to build a wardrobe with these colors?
          </Text>
          <Text
            style={[styles.upgradeDescription, { color: colors.secondary }]}
          >
            Upgrade to Premium to organize your clothes by color and get
            personalized outfit recommendations.
          </Text>
          <Button
            title='Upgrade to Premium'
            onPress={goToPremium}
            style={styles.upgradeButton}
            trackClick={true}
            trackingLocation="virtual-drape-test-screen"
            trackingData={{
              button_name: 'Upgrade to Premium',
              button_type: 'upgrade_to_premium',
              screen: 'virtual_drape_test',
            }}
          />
        </View>
      )} */}
      </Animated.View>
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <ScrollView
        style={[
          styles.scrollView,
          { paddingHorizontal: isMobile ? containerPadding : 300 },
        ]}
        contentContainerStyle={[
          styles.scrollContent,
          {
            padding: isMobile ? 12 : 0,
            paddingTop: isMobile ? 0 : 16,
            minWidth: !isMobile ? 800 : null,
            alignSelf: 'center',
            alignItems: isMobile ? 'center' : 'stretch',
            justifyContent: isMobile ? 'center' : 'flex-start',
            minHeight: isMobile ? availableHeight : undefined,
            flexGrow: isMobile ? 1 : undefined,
          },
        ]}
        showsVerticalScrollIndicator={false}
      >
        <PageHero
          title="Virtual Drape Test"
          subtitle="See how colours look on you in real time. Use your camera or a photo."
        />

        {isCameraMode ? (
          <>
            <View
              style={[styles.realTimeHeader, { backgroundColor: colors.card }]}
            >
              <View style={styles.realTimeContent}>
                <Ionicons
                  name="color-palette"
                  size={24}
                  color={colors.primary}
                />
                <Text style={[styles.realTimeTitle, { color: colors.text }]}>
                  Real-time Colour Testing
                </Text>
              </View>
              <Text
                style={[styles.realTimeSubtitle, { color: colors.secondary }]}
              >
                For best results, use natural daylight, face a window, and
                remove your glasses to reduce glare.
              </Text>
            </View>
            <View
              style={[styles.cameraWrapper, { width: isMobile ? 400 : 400 }]}
            >
              <CameraComponent
                captureOvalOnly
                onImageCaptured={async (uri, detectedSkinTone) => {
                  trackVirtualDrapeUsage();

                  // Set analysis state immediately to show progress
                  setIsAnalyzing(true);
                  setAnalysisProgress(0);
                  setHasCompletedAnalysis(false);
                  setIsAIPaletteLoading(false);
                  aiPaletteImageRef.current = null;

                  setFaceImage(uri);
                  setImageSourceType('camera');
                  setSkinTone(detectedSkinTone || null);
                  setIsCameraMode(false);
                  setIsShowingRecommendedColors(false);

                  // Automatically crop image to focus on face
                  await autoCropImageToFace(uri);

                  // Analyze skin undertone from captured image
                  await analyzeImageUndertone(uri);
                  await applyAIPaletteForImage(uri, detectedSkinTone || null);
                }}
                onClose={() => setIsCameraMode(false)}
              />
            </View>
          </>
        ) : !faceImage ? (
          renderUploadSection()
        ) : (
          <>
            {(isAnalyzing ||
              isAIPaletteLoading ||
              (isLoadingRecent && analysisProgress > 0)) && (
              <View
                style={[
                  styles.progressContainer,
                  {
                    backgroundColor: colors.card,
                    borderColor: colors.border,
                    margin: isMobile ? 12 : 16,
                    padding: isMobile ? 16 : 20,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.progressText,
                    { color: colors.text, fontSize: isMobile ? 14 : 16 },
                  ]}
                >
                  {isLoadingRecent
                    ? 'Loading Your Analysis...'
                    : isAIPaletteLoading
                      ? 'Enhancing Palette with AI...'
                      : 'Building Your Palette'}
                </Text>
                <View
                  style={[
                    styles.progressBar,
                    { backgroundColor: colors.border },
                  ]}
                >
                  <Animated.View
                    style={[
                      styles.progressFill,
                      {
                        backgroundColor: colors.primary,
                        width: `${analysisProgress}%`,
                      },
                    ]}
                  />
                </View>
              </View>
            )}
            {!isAnalyzing && !isLoadingRecent && renderDrapeTestSection()}
          </>
        )}
      </ScrollView>

      {ENABLE_AI_FEATURES &&
        !isPublicPreview &&
        !!faceImage &&
        !isCameraMode &&
        !isAnalyzing &&
        !aiInsightsModalVisible &&
        (Platform.OS === 'web' ? (
          <FloatingActionButton
            bottom={bottomNavHeight + 16 + insets.bottom}
            right={16 + insets.right}
            onPress={() => {
              trackButtonClick(
                'AI insights FAB',
                'virtual-drape-screen-ai-insights-fab'
              );
              setAiInsightsModalVisible(true);
            }}
          />
        ) : (
          <FloatingActionButtonOverlay>
            <FloatingActionButton
              viewportFixed={false}
              bottom={bottomNavHeight + 16 + insets.bottom}
              right={16 + insets.right}
              onPress={() => {
                trackButtonClick(
                  'AI insights FAB',
                  'virtual-drape-screen-ai-insights-fab'
                );
                setAiInsightsModalVisible(true);
              }}
            />
          </FloatingActionButtonOverlay>
        ))}

      {ENABLE_AI_FEATURES && (
        <PremiumAIColorChatModal
          visible={aiInsightsModalVisible}
          onClose={() => setAiInsightsModalVisible(false)}
          fabBottom={bottomNavHeight + 16 + insets.bottom}
          fabRight={16 + insets.right}
          title="Virtual Drape AI prompts"
          promptSectionKeys={['virtual_drape']}
          detectedSeason={undertone}
          undertone={undertone ? legacyUndertoneForApi(undertone) : undefined}
          skinToneCategory={
            skinTone?.category ||
            skinUndertoneService.getSkinToneData()?.skinToneCategory ||
            undefined
          }
          dominantColors={
            skinUndertoneService
              .getSkinToneData()
              ?.dominantColors?.map((c) => ({ hex: c.hex })) ?? undefined
          }
        />
      )}

      {/* Shareable Card Modal */}
      <Modal
        visible={isCreatingCard}
        transparent={true}
        animationType="fade"
        onRequestClose={cancelShareCard}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[
              styles.modalContent,
              { backgroundColor: colors.background },
            ]}
          >
            <Text style={[styles.modalTitle, { color: colors.text }]}>
              Your Colour Card
            </Text>

            <ViewShot
              ref={shareCardRef}
              options={{
                format: 'jpg',
                quality: 0.9,
                result: 'data-uri',
              }}
              style={[
                styles.shareCard,
                {
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                },
              ]}
            >
              <View style={styles.shareCardContent}>
                <View style={styles.shareCardHeader}>
                  <Text
                    style={[
                      styles.shareCardAppName,
                      { color: colors.theme_color },
                    ]}
                  >
                    Coloursense
                  </Text>
                  <Text style={[styles.shareCardTitle, { color: colors.text }]}>
                    My Colour Analysis
                  </Text>
                </View>

                <View style={styles.shareCardBody}>
                  <Image
                    source={{ uri: croppedFaceImage || faceImage || '' }}
                    style={[
                      styles.shareCardImage,
                      { borderColor: colors.theme_color },
                    ]}
                  />

                  <View style={styles.shareCardInfo}>
                    <Text
                      style={[styles.shareCardPalette, { color: colors.text }]}
                    >
                      {selectedPalette.name} Palette
                    </Text>

                    <View
                      style={[
                        styles.shareCardColorRow,
                        { backgroundColor: colors.border },
                      ]}
                    >
                      {selectedColor && (
                        <View
                          style={[
                            styles.shareCardColorSwatch,
                            {
                              backgroundColor: selectedColor,
                              borderColor: colors.borderLight,
                            },
                          ]}
                        />
                      )}
                      <Text
                        style={[
                          styles.shareCardColorName,
                          { color: colors.text },
                        ]}
                      >
                        {selectedColor ? getColorName(selectedColor) : ''}
                      </Text>
                    </View>

                    <Text
                      style={[
                        styles.shareCardFeedback,
                        { color: colors.secondary },
                      ]}
                    >
                      {colorAnalysis
                        ? colorAnalysis.feedback
                        : 'This colour has been analyzed against your skin tone.'}
                    </Text>
                  </View>
                </View>

                <View
                  style={[
                    styles.shareCardFooter,
                    { borderTopColor: colors.border },
                  ]}
                >
                  <Text
                    style={[
                      styles.shareCardFooterText,
                      { color: colors.secondary },
                    ]}
                  >
                    Created with Coloursense
                  </Text>

                  <View style={styles.shareCardButtons}>
                    <Button
                      title="Cancel"
                      onPress={cancelShareCard}
                      variant="outline"
                      style={styles.shareCardButton}
                      hoverColor={colors.authBackground}
                      trackClick={true}
                      trackingLocation="virtual-drape-test-screen"
                      trackingData={{
                        button_name: 'Cancel Shareable Colour Card',
                        button_type: 'cancel_shareable_colour_card',
                        screen: 'virtual_drape_test',
                      }}
                    />
                    <Button
                      title={shareLoading ? 'Processing...' : 'Share Card'}
                      onPress={shareCard}
                      disabled={shareLoading}
                      style={styles.shareCardButton}
                      trackClick={true}
                      trackingLocation="virtual-drape-test-screen"
                      trackingData={{
                        button_name: 'Share Card',
                        button_type: 'share_card',
                        screen: 'virtual_drape_test',
                      }}
                    />
                  </View>
                </View>
              </View>
            </ViewShot>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// Helper functions for generating feedback
const getColorName = (hexColor: string) => {
  // This would ideally use a color naming library
  // For demo purposes, use a simple lookup
  const colorMap: Record<string, string> = {
    '#C4DFDF': 'light blue',
    '#D2E0FB': 'soft blue',
    '#FFEAD2': 'peachy beige',
    '#F1F7B5': 'light yellow',
    '#A8DF8E': 'mint green',
    '#E3F2C1': 'light lime',
    '#8ECAE6': 'sky blue',
    '#219EBC': 'teal blue',
    '#023047': 'navy blue',
    '#FFB703': 'amber',
    '#FB8500': 'orange',
    '#FFE5E5': 'pale pink',
    '#865439': 'chocolate brown',
    '#C68B59': 'caramel',
    '#D7B19D': 'tan',
    '#8E3200': 'rust',
    '#A64B2A': 'terracotta',
    '#D8AD7F': 'sand',
    '#27374D': 'slate',
    '#526D82': 'steel blue',
    '#9DB2BF': 'pewter',
    '#DDE6ED': 'pale gray',
    '#102C57': 'midnight blue',
    '#0766AD': 'cobalt blue',
    '#F8F0E5': 'cream',
    '#EADBC8': 'beige',
    '#DAC0A3': 'taupe',
    '#0F2C59': 'dark navy',
    '#F8F9FA': 'off-white',
    '#CED4DA': 'light gray',
    '#CD7F32': 'copper brown',
    '#E8B4B8': 'soft pink',
  };

  return colorMap[hexColor] || 'selected';
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },

  shareButton: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  scrollView: {
    flex: 1,
  },

  scrollContent: {
    padding: 16,
    paddingTop: 0,
  },

  premiumBanner: {
    borderRadius: 16,
    marginBottom: 24,
    overflow: 'hidden',
    borderWidth: 1,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },

  premiumBannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },

  premiumIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },

  premiumIcon: {
    fontSize: 20,
  },

  premiumTextContainer: {
    flex: 1,
  },

  premiumBannerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 2,
  },

  premiumBannerText: {
    fontSize: 14,
  },

  premiumBannerArrow: {
    fontSize: 24,
    marginLeft: 8,
  },
  uploadSection: {
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 20,
    paddingVertical: 20,
    backgroundColor: 'white',
  },

  title: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
    marginTop: 0,
    textAlign: 'center',
  },

  subtitle: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },

  uploadContainer: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: 16,
    padding: 30,
    marginBottom: 24,
    width: '75%',
    alignSelf: 'center',
    minHeight: 200,
  },

  uploadTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 5,
  },

  uploadIconContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },

  uploadIcon: {
    width: 64,
    height: 64,
    justifyContent: 'center',
    alignItems: 'center',
  },

  uploadText: {
    fontSize: 13,
  },

  cameraButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    width: '100%',
    justifyContent: 'center',
    marginBottom: 10,
  },

  cameraText: {
    fontSize: 16,
    fontWeight: '500',
    flexShrink: 0,
    ...(Platform.OS === 'web' && ({ whiteSpace: 'nowrap' } as TextStyle)),
  },

  drapeTestSection: {
    marginBottom: 16,
  },

  imagePreviewContainer: {
    alignItems: 'center',
    marginBottom: 16,
    gap: 12,
    borderWidth: 1,
    borderRadius: 16,
    padding: 20,
    minWidth: 200,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },

  imagePreviewResults: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
    padding: 12,
    gap: 12,
  },

  imagePreviewCard: {
    flex: 1,
    padding: 16,
    alignItems: 'center',
    minWidth: 0,
  },

  analysisCardsContainer: {
    flex: 1,
    gap: 8,
    minWidth: 0,
  },

  imagePreview: {
    width: '50%',
    height: 350,
    borderRadius: 12,
    marginBottom: 12,
  },

  imageActions: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },

  imageActionsMobile: {
    marginBottom: 100,
  },

  changeImageButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    width: 140,
    height: 40,
  },

  skinToneTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
  },

  skinToneInfo: {
    alignItems: 'center',
  },

  colorSwatch: {
    width: Platform.OS === 'web' ? 48 : 36,
    height: Platform.OS === 'web' ? 48 : 36,
    borderRadius: Platform.OS === 'web' ? 10 : 8,
    marginBottom: 12,
    borderWidth: 1.5,
  },

  skinToneConfidence: {
    fontSize: 14,
  },

  drapeContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    marginBottom: 24,
  },

  faceDetectionStatus: {
    position: 'absolute',
    top: -40,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    zIndex: 10,
  },

  faceDetectionText: {
    fontSize: 12,
    fontWeight: '500',
  },

  faceDetectionError: {
    position: 'absolute',
    top: -40,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    zIndex: 10,
  },

  faceDetectionErrorText: {
    fontSize: 11,
    fontWeight: '500',
  },

  faceDetectionSuccess: {
    position: 'absolute',
    top: -40,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    zIndex: 10,
  },

  faceDetectionSuccessText: {
    fontSize: 11,
    fontWeight: '500',
  },

  wheelOverlay: {
    position: 'absolute',
  },

  paletteSelector: {
    marginBottom: 20,
  },

  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    marginBottom: 8,
    marginTop: 0,
  },

  palettesScrollView: {
    marginBottom: 4,
  },

  palettesContainer: {
    paddingHorizontal: 4,
  },

  paletteButton: {
    borderRadius: 20,
    marginRight: 8,
    minWidth: 0,
  },

  paletteButtonTouchable: {
    paddingVertical: 8,
    paddingHorizontal: 16,
  },

  paletteButtonText: {
    fontWeight: '500',
  },

  colorsSection: {
    marginBottom: 24,
  },

  colorInstructions: {
    fontSize: 12,
    marginBottom: 16,
  },

  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },

  feedbackSection: {
    marginBottom: 8,
  },

  feedbackCard: {
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },

  feedbackTitle: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 8,
  },

  feedbackText: {
    fontSize: 14,
    lineHeight: 22,
  },

  upgradeCTA: {
    padding: 20,
    borderRadius: 16,
    marginTop: 24,
    marginBottom: 16,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },

  upgradeTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },

  upgradeDescription: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },

  upgradeButton: {
    marginBottom: 0,
  },

  shareActionButton: {
    alignSelf: 'center',
    marginTop: 16,
    padding: 10,
    borderRadius: 20,
    flexDirection: 'row',
    alignItems: 'center',
  },

  shareActionButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },

  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: 16,
  },

  modalContent: {
    padding: 20,
    borderRadius: 20,
    width: '90%',
    maxWidth: 400,
    maxHeight: '85%',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 16,
  },

  shareCard: {
    width: '100%',
    maxWidth: 320,
    minHeight: 400,
    aspectRatio: 2 / 3,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
  },

  shareCardContent: {
    flex: 1,
    padding: 16,
    justifyContent: 'space-between',
  },

  shareCardHeader: {
    alignItems: 'center',
    marginBottom: 16,
  },

  shareCardAppName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },

  shareCardTitle: {
    fontSize: 16,
    fontWeight: '600',
  },

  shareCardBody: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },

  shareCardImage: {
    width: 120,
    height: 120,
    borderRadius: 60,
    marginBottom: 16,
    borderWidth: 3,
  },

  shareCardInfo: {
    alignItems: 'center',
    width: '100%',
    flex: 1,
    justifyContent: 'center',
  },

  shareCardPalette: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
  },

  shareCardColorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    padding: 8,
    borderRadius: 8,
    width: '100%',
    justifyContent: 'center',
  },

  shareCardColorSwatch: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 12,
    borderWidth: 1,
  },

  shareCardColorName: {
    fontSize: 16,
    fontWeight: '500',
  },

  shareCardFeedback: {
    fontSize: 14,
    textAlign: 'center',
    paddingHorizontal: 10,
  },

  shareCardFooter: {
    alignItems: 'center',
    paddingTop: 12,
    borderTopWidth: 1,
  },

  shareCardFooterText: {
    fontSize: 12,
    marginBottom: 16,
  },

  shareCardButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    gap: 8,
  },

  shareCardButton: {
    flex: 1,
  },

  buttonContainer: {
    flexDirection: 'row',
    gap: 10,
    alignSelf: 'center',
  },

  cameraWrapper: {
    alignSelf: 'center',
    aspectRatio: 4 / 5,
    marginVertical: 20,
    borderRadius: 12,
    overflow: 'hidden',
  },

  realTimeHeader: {
    padding: 20,
    borderRadius: 12,
    marginBottom: 5,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },

  realTimeContent: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },

  realTimeTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },

  realTimeSubtitle: {
    fontSize: 14,
    lineHeight: 20,
  },
  colorScoreCard: {
    padding: 12,
    minWidth: 160,
  },

  colorScaleContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 12,
    gap: 8,
  },

  colorScaleBar: {
    flexDirection: 'row',
    width: '100%',
    height: 28,
    borderRadius: 4,
    overflow: 'hidden',
    gap: 2,
  },

  colorScaleBlock: {
    flex: 1,
    height: '100%',
  },

  colorScaleDivider: {
    width: 2,
    height: '100%',
    marginHorizontal: 2,
  },

  scaleLabelsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },

  scaleLabel: {
    fontSize: 14,
    fontWeight: '600',
  },

  colorScoreTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },

  analyzingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    gap: 8,
  },

  analyzingText: {
    fontSize: 14,
    fontStyle: 'italic',
  },

  palettesSection: {
    marginBottom: 8,
  },

  paletteButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },

  selectedPaletteSection: {
    marginBottom: 8,
  },

  selectedPaletteSectionRecommendedMobile: {
    marginTop: 20,
  },

  sectionTitleRecommendedMobile: {
    marginTop: 0,
  },

  colorSwatches: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Platform.OS === 'web' ? 10 : 8,
  },

  gildedGlowTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
  },

  gildedGlowOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },

  gildedGlowOption: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 20,
    borderWidth: 1,
  },

  gildedGlowSwatch: {
    width: Platform.OS === 'web' ? 48 : 36,
    height: Platform.OS === 'web' ? 48 : 36,
    borderRadius: Platform.OS === 'web' ? 10 : 8,
    marginBottom: 8,
  },

  gildedGlowText: {
    fontSize: 14,
    fontWeight: '500',
  },

  progressContainer: {
    margin: 12,
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },

  progressText: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 16,
  },

  progressBar: {
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
  },

  progressFill: {
    height: '100%',
    borderRadius: 4,
  },

  publicPreviewBlur: {
    marginBottom: 24,
  },
});

// Web-specific upload container with drag and drop
const WebUploadContainer = ({
  colors,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onPickImage,
  onTakeSelfie,
  isAnalyzing,
  // isPremium,
  isLoadingRecent,
  onLoadRecentAnalysis,
  hideColorAnalysisButton = false,
}: {
  colors: Record<string, string>;
  isDragOver: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent<HTMLElement>) => void;
  onPickImage: () => void;
  onTakeSelfie: () => void;
  isAnalyzing: boolean;
  // isPremium: boolean;
  isLoadingRecent: boolean;
  onLoadRecentAnalysis: () => void;
  hideColorAnalysisButton?: boolean;
}) => {
  const { isMobile } = useResponsiveLayout();

  if (Platform.OS !== 'web') return null;

  return (
    <div
      style={{
        borderWidth: 2,
        borderStyle: 'dashed',
        borderRadius: 16,
        padding: 30,
        marginBottom: 24,
        width: '75%',
        alignSelf: 'center',
        minHeight: 200,
        borderColor: isDragOver ? colors.primary : colors.theme_color,
        backgroundColor: isDragOver ? `${colors.primary}10` : 'transparent',
        transition: 'all 0.2s ease',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: 24,
          cursor: 'pointer',
        }}
        onClick={onPickImage}
      >
        <div
          style={{
            width: 64,
            height: 64,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Ionicons name="images" size={32} color={colors.primary} />
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 'bold',
            textAlign: 'center',
            marginBottom: 5,
            color: colors.text,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            lineHeight: '20px',
          }}
        >
          Upload an Image
        </div>
        <div
          style={{
            fontSize: 12,
            color: colors.secondary,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            lineHeight: '16px',
            textAlign: 'center',
          }}
        >
          Choose from Gallery or drag and drop an image here
        </div>
      </div>

      <Button
        title="Real-time Testing"
        onPress={onTakeSelfie}
        icon={<Ionicons name="camera" size={20} color={colors.card} />}
        style={styles.cameraButton}
        textStyle={{ ...styles.cameraText, ...(isMobile && { fontSize: 14 }) }}
        variant="primary"
        trackClick={true}
        trackingLocation="virtual-drape-test-screen"
        trackingData={{
          button_name: 'Real-time Testing',
          button_type: 'real_time_testing',
          screen: 'virtual_drape_test',
        }}
      />

      {isAnalyzing && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 16,
            gap: 8,
          }}
        ></div>
      )}

      {!hideColorAnalysisButton && (
        <Button
          title={
            isLoadingRecent
              ? 'Loading Recent Analysis...'
              : 'Colour Analysis Results'
          }
          onPress={onLoadRecentAnalysis}
          disabled={isLoadingRecent}
          icon={
            <Ionicons name="color-palette" size={20} color={colors.primary} />
          }
          style={styles.cameraButton}
          textStyle={{
            ...styles.cameraText,
            ...(isMobile && { fontSize: 14 }),
          }}
          variant="outline"
          hoverColor={colors.authBackground}
          trackClick={true}
          trackingLocation="virtual-drape-test-screen"
          trackingData={{
            button_name: 'Colour Analysis Results',
            button_type: 'colour_analysis_results',
            screen: 'virtual_drape_test',
          }}
        />
      )}
    </div>
  );
};

export default VirtualDrapeTestScreen;
